import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { FileText, Filter, Eye, ChevronLeft, ChevronRight, RotateCcw, Trash2, Loader2, Package, AlertTriangle, Shield, Globe, Monitor, Download, Calendar, BarChart3, FileSpreadsheet, Users as UsersIcon } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, getMovementTypeColor, getMovementTypeLabel } from '../lib/utils';

const AuditPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [deletedMovements, setDeletedMovements] = useState([]);
  const [bomberazos, setBomberazos] = useState(null);
  const [bomberazosLoading, setBomberazosLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [bomberazoDays, setBomberazoDays] = useState(30);
  const pageSize = 20;
  
  // User Activity state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilters, setActivityFilters] = useState({
    user_id: 'all',
    action_type: 'all'
  });
  
  // Movement Reports state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    startDate: '',
    endDate: '',
    type: 'all',
    responsible_id: 'all',
    status: 'all'
  });
  const [responsibles, setResponsibles] = useState([]);
  
  const [adminCreds, setAdminCreds] = useState({
    admin_username: '',
    admin_password: ''
  });
  
  const [filters, setFilters] = useState({
    user_id: 'all',
    action: 'all',
    entity: 'all',
    startDate: '',
    endDate: ''
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Load user activity logs
  const loadActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({
        page_num: activityPage.toString(),
        per_page: '20'
      });
      if (activityFilters.user_id !== 'all') params.append('user_id', activityFilters.user_id);
      if (activityFilters.action_type !== 'all') params.append('action_type', activityFilters.action_type);
      
      const res = await api().get(`/activity/logs?${params}`);
      setActivityLogs(res.data.logs || []);
      setActivityTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      toast.error('Error al cargar actividad de usuarios');
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page]);

  const loadBomberazos = async (days = 30) => {
    setBomberazosLoading(true);
    try {
      const response = await api().get(`/audit/bomberazos?days=${days}`);
      setBomberazos(response.data);
    } catch (error) {
      toast.error('Error al cargar bomberazos');
    } finally {
      setBomberazosLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      
      const params = new URLSearchParams();
      if (filters.user_id !== 'all') params.append('user_id', filters.user_id);
      if (filters.action !== 'all') params.append('action', filters.action);
      if (filters.entity !== 'all') params.append('entity', filters.entity);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);

      const [auditRes, usersRes, deletedRes, responsiblesRes] = await Promise.all([
        apiInstance.get(`/audit?${params}`),
        apiInstance.get('/users'),
        apiInstance.get('/audit/deleted-movements'),
        apiInstance.get('/responsibles')
      ]);

      setAuditLogs(auditRes.data);
      setUsers(usersRes.data);
      setDeletedMovements(deletedRes.data);
      setResponsibles(responsiblesRes.data || []);
    } catch (error) {
      toast.error('Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setPage(0);
    loadData();
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    setIsDetailModalOpen(true);
  };

  const openRestoreModal = (movement) => {
    setSelectedMovement(movement);
    setAdminCreds({ admin_username: '', admin_password: '' });
    setIsRestoreModalOpen(true);
  };

  // Generate and download movement report as CSV
  const downloadMovementReport = async () => {
    setReportLoading(true);
    try {
      const apiInstance = api();
      
      const params = new URLSearchParams();
      if (reportFilters.startDate) params.append('start_date', reportFilters.startDate);
      if (reportFilters.endDate) params.append('end_date', reportFilters.endDate);
      if (reportFilters.type !== 'all') params.append('type', reportFilters.type);
      if (reportFilters.responsible_id !== 'all') params.append('responsible_id', reportFilters.responsible_id);
      if (reportFilters.status !== 'all') params.append('status', reportFilters.status);
      
      const response = await apiInstance.get(`/reports/movements?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `reporte_movimientos_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte descargado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setReportLoading(false);
    }
  };

  // v5.4.24 — Consolidated Excel report (per user or system-wide)
  const [consolidatedFilters, setConsolidatedFilters] = useState({
    user_id: 'all',
    startDate: '',
    endDate: '',
    include_activities: true,
    include_audit: true,
    include_movements: true,
    include_notifications: false,
    compare: true,  // v5.4.26 — Comparison mode for PDF (only affects PDF button)
  });
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);

  const downloadFile = async (path, filename) => {
    const apiInstance = api();
    const response = await apiInstance.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadConsolidatedReport = async () => {
    setConsolidatedLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('user_id', consolidatedFilters.user_id);
      if (consolidatedFilters.startDate) params.append('start_date', consolidatedFilters.startDate);
      if (consolidatedFilters.endDate) params.append('end_date', consolidatedFilters.endDate);
      params.append('include_activities', String(consolidatedFilters.include_activities));
      params.append('include_audit', String(consolidatedFilters.include_audit));
      params.append('include_movements', String(consolidatedFilters.include_movements));
      params.append('include_notifications', String(consolidatedFilters.include_notifications));
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
      const who = consolidatedFilters.user_id === 'all' ? 'todos' : consolidatedFilters.user_id;
      await downloadFile(`/reports/user-consolidated/excel?${params}`, `reporte_auditoria_${who}_${stamp}.xlsx`);
      toast.success('Reporte consolidado descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar reporte consolidado');
    } finally {
      setConsolidatedLoading(false);
    }
  };

  // v5.4.25 — Executive PDF report
  const [execPdfLoading, setExecPdfLoading] = useState(false);
  const downloadExecutivePdf = async () => {
    setExecPdfLoading(true);
    try {
      const params = new URLSearchParams();
      if (consolidatedFilters.user_id && consolidatedFilters.user_id !== 'all') {
        params.append('user_id', consolidatedFilters.user_id);
      }
      if (consolidatedFilters.startDate) params.append('start_date', consolidatedFilters.startDate);
      if (consolidatedFilters.endDate) params.append('end_date', consolidatedFilters.endDate);
      // If no dates, default to last 30 days
      if (!consolidatedFilters.startDate && !consolidatedFilters.endDate) {
        params.append('days', '30');
      }
      if (consolidatedFilters.compare) params.append('compare', 'true');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
      await downloadFile(`/reports/executive-audit/pdf?${params}`, `reporte_ejecutivo_${stamp}.pdf`);
      toast.success('Reporte ejecutivo PDF descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar PDF ejecutivo');
    } finally {
      setExecPdfLoading(false);
    }
  };

  const downloadAuditLogsExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.user_id !== 'all') params.append('user_id', filters.user_id);
      if (filters.action !== 'all') params.append('action', filters.action);
      if (filters.entity !== 'all') params.append('entity', filters.entity);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      const stamp = new Date().toISOString().split('T')[0];
      await downloadFile(`/reports/audit-logs/excel?${params}`, `auditoria_${stamp}.xlsx`);
      toast.success('Auditoría descargada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar auditoría');
    }
  };

  const downloadActivityExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (activityFilters.user_id !== 'all') params.append('user_id', activityFilters.user_id);
      if (activityFilters.action_type !== 'all') params.append('action_type', activityFilters.action_type);
      const stamp = new Date().toISOString().split('T')[0];
      await downloadFile(`/reports/user-activity/excel?${params}`, `actividad_usuarios_${stamp}.xlsx`);
      toast.success('Actividad descargada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar actividad');
    }
  };

  const handleRestore = async () => {
    if (!adminCreds.admin_username || !adminCreds.admin_password) {
      toast.error('Ingresa las credenciales de administrador');
      return;
    }

    setSaving(true);
    try {
      const apiInstance = api();
      await apiInstance.post(`/movements/${selectedMovement.id}/restore`, adminCreds);
      toast.success('Movimiento restaurado exitosamente');
      setIsRestoreModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    } finally {
      setSaving(false);
    }
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      create: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      update: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      delete: 'bg-red-500/20 text-red-400 border-red-500/30',
      restore: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      login: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      logout: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      approve: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      override: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      failed_login: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[action] || 'bg-gray-500/20 text-gray-400';
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'Crear',
      update: 'Actualizar',
      delete: 'Eliminar',
      restore: 'Restaurar',
      login: 'Iniciar sesión',
      logout: 'Cerrar sesión',
      approve: 'Aprobar',
      override: 'Override Admin',
      failed_login: 'Login Fallido'
    };
    return labels[action] || action;
  };

  const getEntityLabel = (entity) => {
    const labels = {
      users: 'Usuarios',
      equipment: 'Equipos',
      categories: 'Categorías',
      locations: 'Ubicaciones',
      responsibles: 'Responsables',
      movements: 'Movimientos',
      events: 'Eventos',
      exports: 'Exportaciones',
      auth: 'Autenticación',
      email: 'Email'
    };
    return labels[entity] || entity;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Auditoría</h1>
          <p className="text-muted-foreground mt-1">
            Registro completo de actividades del sistema
          </p>
        </div>
      </div>

      <Tabs defaultValue="logs" className="space-y-6" onValueChange={(val) => {
        if (val === 'bomberazos' && !bomberazos) loadBomberazos(bomberazoDays);
        if (val === 'activity' && activityLogs.length === 0) loadActivityLogs();
      }}>
        <TabsList>
          <TabsTrigger value="logs">Registro de Actividad</TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            Actividad de Usuarios
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            Reportes
          </TabsTrigger>
          <TabsTrigger value="deleted">
            Movimientos Eliminados 
            {deletedMovements.length > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400">{deletedMovements.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bomberazos" className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Bomberazos
          </TabsTrigger>
        </TabsList>

        {/* Audit Logs Tab */}
        <TabsContent value="logs" className="space-y-6">
          {/* Filters */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5 text-primary" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="space-y-2">
                  <Label>Usuario</Label>
                  <Select
                    value={filters.user_id}
                    onValueChange={(value) => setFilters({ ...filters, user_id: value })}
                  >
                    <SelectTrigger data-testid="audit-user-filter">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Acción</Label>
                  <Select
                    value={filters.action}
                    onValueChange={(value) => setFilters({ ...filters, action: value })}
                  >
                    <SelectTrigger data-testid="audit-action-filter">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="create">Crear</SelectItem>
                      <SelectItem value="update">Actualizar</SelectItem>
                      <SelectItem value="delete">Eliminar</SelectItem>
                      <SelectItem value="restore">Restaurar</SelectItem>
                      <SelectItem value="login">Iniciar sesión</SelectItem>
                      <SelectItem value="override">Override</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Entidad</Label>
                  <Select
                    value={filters.entity}
                    onValueChange={(value) => setFilters({ ...filters, entity: value })}
                  >
                    <SelectTrigger data-testid="audit-entity-filter">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="movements">Movimientos</SelectItem>
                      <SelectItem value="equipment">Equipos</SelectItem>
                      <SelectItem value="users">Usuarios</SelectItem>
                      <SelectItem value="categories">Categorías</SelectItem>
                      <SelectItem value="locations">Ubicaciones</SelectItem>
                      <SelectItem value="responsibles">Responsables</SelectItem>
                      <SelectItem value="exports">Exportaciones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    data-testid="audit-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    data-testid="audit-end-date"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="btn-primary flex-1" data-testid="apply-audit-filters-btn">
                    Filtrar
                  </Button>
                  <Button onClick={downloadAuditLogsExcel} variant="outline" title="Descargar Excel" data-testid="download-audit-excel-btn">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Table */}
          <Card className="card-hover">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Entidad</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(10)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={6}>
                          <Skeleton className="h-12 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">No hay registros de auditoría</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.map((log) => (
                      <TableRow key={log.id} className="table-row" data-testid={`audit-row-${log.id}`}>
                        <TableCell className="text-sm">{formatDate(log.date)}</TableCell>
                        <TableCell>{log.user_name || 'Sistema'}</TableCell>
                        <TableCell>
                          <Badge className={getActionBadgeColor(log.action)}>
                            {getActionLabel(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell>{getEntityLabel(log.entity)}</TableCell>
                        <TableCell className="max-w-xs truncate text-muted-foreground">
                          {log.description}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => openDetailModal(log)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {!loading && auditLogs.length > 0 && (
                <div className="border-t border-border/40 px-6 py-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {page * pageSize + 1} - {page * pageSize + auditLogs.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={auditLogs.length < pageSize}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Actividad de Usuarios en Tiempo Real
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <Select 
                  value={activityFilters.user_id} 
                  onValueChange={(val) => setActivityFilters(f => ({...f, user_id: val}))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={activityFilters.action_type} 
                  onValueChange={(val) => setActivityFilters(f => ({...f, action_type: val}))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Tipo de Acción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las acciones</SelectItem>
                    <SelectItem value="exclude_visibility">Excluir visibilidad</SelectItem>
                    <SelectItem value="navigation">Navegación</SelectItem>
                    <SelectItem value="click">Clicks</SelectItem>
                    <SelectItem value="filter">Filtros</SelectItem>
                    <SelectItem value="action">Acciones</SelectItem>
                    <SelectItem value="download">Descargas</SelectItem>
                    <SelectItem value="visibility">Visibilidad (Tab)</SelectItem>
                    <SelectItem value="tab_change">Cambio de Tabs</SelectItem>
                    <SelectItem value="search">Búsquedas</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={loadActivityLogs} disabled={activityLoading}>
                  {activityLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4 mr-2" />}
                  Aplicar
                </Button>
                <Button onClick={downloadActivityExcel} variant="outline" title="Descargar Excel" data-testid="download-activity-excel-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Excel
                </Button>
              </div>

              {activityLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Eye className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No hay actividad registrada</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    La actividad de usuarios se registra automáticamente cuando navegan y realizan acciones
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha/Hora</TableHead>
                          <TableHead>Usuario</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Acción</TableHead>
                          <TableHead>Página</TableHead>
                          <TableHead>IP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="text-sm">
                              {new Date(log.timestamp).toLocaleString('es-MX', {
                                month: 'short', day: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                                  {log.username?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <span className="font-medium">{log.username}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                log.action_type === 'navigation' ? 'bg-blue-500/10 text-blue-500' :
                                log.action_type === 'action' ? 'bg-green-500/10 text-green-500' :
                                log.action_type === 'download' ? 'bg-purple-500/10 text-purple-500' :
                                log.action_type === 'filter' ? 'bg-amber-500/10 text-amber-500' :
                                'bg-gray-500/10'
                              }>
                                {log.action_type === 'navigation' ? 'Navegación' :
                                 log.action_type === 'action' ? 'Acción' :
                                 log.action_type === 'download' ? 'Descarga' :
                                 log.action_type === 'filter' ? 'Filtro' :
                                 log.action_type === 'tab_change' ? 'Tab' :
                                 log.action_type === 'search' ? 'Búsqueda' :
                                 log.action_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[250px] truncate">
                              {log.action}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {log.page}
                            </TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">
                              {log.ip_address?.split(',')[0]}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {activityTotal} registro(s) encontrado(s)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setActivityPage(p => Math.max(1, p - 1)); loadActivityLogs(); }}
                        disabled={activityPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm">Página {activityPage}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setActivityPage(p => p + 1); loadActivityLogs(); }}
                        disabled={activityLogs.length < 20}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movement Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Generador de Reportes de Movimientos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground text-sm">
                Genera y descarga reportes de movimientos en formato CSV. Puedes filtrar por fechas, tipo de movimiento, responsable y estado.
              </p>
              
              {/* Report Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Fecha Inicio
                  </Label>
                  <Input
                    type="date"
                    value={reportFilters.startDate}
                    onChange={(e) => setReportFilters({...reportFilters, startDate: e.target.value})}
                    data-testid="report-start-date"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Fecha Fin
                  </Label>
                  <Input
                    type="date"
                    value={reportFilters.endDate}
                    onChange={(e) => setReportFilters({...reportFilters, endDate: e.target.value})}
                    data-testid="report-end-date"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tipo de Movimiento</Label>
                  <Select 
                    value={reportFilters.type} 
                    onValueChange={(val) => setReportFilters({...reportFilters, type: val})}
                  >
                    <SelectTrigger data-testid="report-type">
                      <SelectValue placeholder="Todos los tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      <SelectItem value="salida">Salidas</SelectItem>
                      <SelectItem value="retorno">Retornos</SelectItem>
                      <SelectItem value="prestamo">Préstamos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Responsable</Label>
                  <Select 
                    value={reportFilters.responsible_id} 
                    onValueChange={(val) => setReportFilters({...reportFilters, responsible_id: val})}
                  >
                    <SelectTrigger data-testid="report-responsible">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {responsibles.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select 
                    value={reportFilters.status} 
                    onValueChange={(val) => setReportFilters({...reportFilters, status: val})}
                  >
                    <SelectTrigger data-testid="report-status">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Download Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  onClick={downloadMovementReport}
                  disabled={reportLoading}
                  className="btn-primary"
                  data-testid="download-report-btn"
                >
                  {reportLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Descargar Reporte CSV
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Info Card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/20 rounded-full">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-primary">Formato del Reporte</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    El archivo CSV incluirá: ID, Fecha, Tipo, Evento/Destino, Responsable, Cliente, Estado, Equipos (cantidad), y Notas.
                    Puedes abrir el archivo con Excel, Google Sheets o cualquier programa compatible con CSV.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* v5.4.24 — Reporte Integral (Auditoría consolidada) */}
          <Card className="card-hover" data-testid="consolidated-report-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                Reporte Integral de Auditoría (Excel multi-hoja)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground text-sm">
                Genera un archivo Excel consolidado con toda la trazabilidad del sistema:
                actividades de usuarios, registro de auditoría, movimientos y (opcional)
                notificaciones. Filtra por un usuario específico o descarga el reporte
                general de todos los usuarios.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <UsersIcon className="h-4 w-4 text-muted-foreground" />
                    Usuario
                  </Label>
                  <Select
                    value={consolidatedFilters.user_id}
                    onValueChange={(val) => setConsolidatedFilters({ ...consolidatedFilters, user_id: val })}
                  >
                    <SelectTrigger data-testid="consolidated-user">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los usuarios (general)</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name || u.username}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Fecha Inicio
                  </Label>
                  <Input
                    type="date"
                    value={consolidatedFilters.startDate}
                    onChange={(e) => setConsolidatedFilters({ ...consolidatedFilters, startDate: e.target.value })}
                    data-testid="consolidated-start-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Fecha Fin
                  </Label>
                  <Input
                    type="date"
                    value={consolidatedFilters.endDate}
                    onChange={(e) => setConsolidatedFilters({ ...consolidatedFilters, endDate: e.target.value })}
                    data-testid="consolidated-end-date"
                  />
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-sm font-medium">Secciones a incluir</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={consolidatedFilters.include_activities}
                      onCheckedChange={(v) => setConsolidatedFilters({ ...consolidatedFilters, include_activities: !!v })}
                      data-testid="consolidated-inc-activities"
                    />
                    <span className="text-sm">Actividades (UI)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={consolidatedFilters.include_audit}
                      onCheckedChange={(v) => setConsolidatedFilters({ ...consolidatedFilters, include_audit: !!v })}
                      data-testid="consolidated-inc-audit"
                    />
                    <span className="text-sm">Auditoría</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={consolidatedFilters.include_movements}
                      onCheckedChange={(v) => setConsolidatedFilters({ ...consolidatedFilters, include_movements: !!v })}
                      data-testid="consolidated-inc-movements"
                    />
                    <span className="text-sm">Movimientos</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={consolidatedFilters.include_notifications}
                      onCheckedChange={(v) => setConsolidatedFilters({ ...consolidatedFilters, include_notifications: !!v })}
                      data-testid="consolidated-inc-notifications"
                    />
                    <span className="text-sm">Notificaciones</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-between items-center gap-2 pt-4 border-t flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={consolidatedFilters.compare}
                    onCheckedChange={(v) => setConsolidatedFilters({ ...consolidatedFilters, compare: !!v })}
                    data-testid="consolidated-compare"
                  />
                  <span>Comparar con periodo anterior (mes vs mes) — solo PDF</span>
                </label>
                <div className="flex gap-2">
                  <Button
                    onClick={downloadExecutivePdf}
                    disabled={execPdfLoading}
                    variant="outline"
                    data-testid="download-executive-pdf-btn"
                  >
                    {execPdfLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    PDF Ejecutivo (1 página)
                  </Button>
                  <Button
                    onClick={downloadConsolidatedReport}
                    disabled={consolidatedLoading}
                    className="btn-primary"
                    data-testid="download-consolidated-btn"
                  >
                    {consolidatedLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                    )}
                    Generar Reporte Excel
                  </Button>
                </div>
              </div>

              <div className="rounded-lg bg-muted/40 p-4 text-xs text-muted-foreground">
                <strong className="text-foreground">Hojas incluidas:</strong> Resumen (KPIs por usuario),
                Actividades, Auditoría, Movimientos, Notificaciones (opcional) y Metadata.
                Ideal para auditorías internas, reportes a dirección o cumplimiento normativo.
                <br />
                <strong className="text-foreground">PDF Ejecutivo:</strong> versión de una página con
                KPI cards, gráfica top-10 por usuario y tabla detallada — pensado para presentar a dirección.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deleted Movements Tab */}
        <TabsContent value="deleted" className="space-y-6">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" />
                Movimientos Eliminados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deletedMovements.length === 0 ? (
                <div className="text-center py-12">
                  <RotateCcw className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No hay movimientos eliminados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {deletedMovements.map((mov) => (
                    <div 
                      key={mov.id} 
                      className="p-4 border border-border/40 rounded-lg hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <Badge className={getMovementTypeColor(mov.type)}>
                              {getMovementTypeLabel(mov.type)}
                            </Badge>
                            <span className="text-sm font-medium">{mov.responsible_name}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <p>Fecha original: {formatDate(mov.date)}</p>
                            <p>Eliminado: {formatDate(mov.deleted_at)} por {mov.deleted_by_name}</p>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {mov.items?.slice(0, 3).map((item, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                <Package className="h-3 w-3 mr-1" />
                                {item.equipment_name} x{item.quantity}
                              </Badge>
                            ))}
                            {mov.items?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{mov.items.length - 3} más
                              </Badge>
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRestoreModal(mov)}
                            className="text-primary border-primary/30 hover:bg-primary/10"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restaurar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bomberazos Tab */}
        <TabsContent value="bomberazos" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Bomberazos</p>
                    <p className="text-3xl font-bold text-red-500">
                      {bomberazosLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : bomberazos?.total_bomberazos || 0}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-500/50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div>
                  <p className="text-sm text-muted-foreground">Período</p>
                  <Select value={String(bomberazoDays)} onValueChange={(val) => { setBomberazoDays(Number(val)); loadBomberazos(Number(val)); }}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Últimos 7 días</SelectItem>
                      <SelectItem value="15">Últimos 15 días</SelectItem>
                      <SelectItem value="30">Últimos 30 días</SelectItem>
                      <SelectItem value="60">Últimos 60 días</SelectItem>
                      <SelectItem value="90">Últimos 90 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-hover col-span-2">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-2">Por Usuario</p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {bomberazos?.by_user && Object.entries(bomberazos.by_user).map(([user, count]) => (
                    <div key={user} className="flex justify-between text-sm">
                      <span>{user}</span>
                      <Badge variant="outline" className="text-red-400">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bomberazos Table */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Salidas Sin Revisión (Bomberazos)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bomberazosLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : !bomberazos?.movements?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No hay bomberazos en el período seleccionado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha/Hora</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead>Equipos</TableHead>
                      <TableHead>Override Por</TableHead>
                      <TableHead>Evento/Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bomberazos.movements.map((mov) => (
                      <TableRow key={mov.id} className="hover:bg-red-500/5">
                        <TableCell className="text-sm">
                          {formatDate(mov.date)}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{mov.responsible_name || 'N/A'}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <span>{mov.items?.length || 0} equipos</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {mov.override_by_name ? (
                            <Badge className="bg-amber-500/20 text-amber-400">
                              {mov.override_by_name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {mov.event_name || mov.loan_reason || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Detalle de Auditoría</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              {/* Special display for failed login attempts */}
              {selectedLog.action === 'failed_login' && selectedLog.details && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-red-400 font-medium">
                    <AlertTriangle className="h-5 w-5" />
                    Intento de Login Fallido
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-background/50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Usuario Intentado</Label>
                      <p className="font-mono font-medium text-lg">
                        {selectedLog.details.username || selectedLog.details.username_attempted || 'Desconocido'}
                      </p>
                    </div>
                    <div className="p-3 bg-background/50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Intentos Fallidos</Label>
                      <p className="font-mono font-medium text-lg text-red-400">
                        {selectedLog.details.failed_attempts || 1}
                      </p>
                    </div>
                    <div className="p-3 bg-background/50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Dirección IP</Label>
                      <p className="font-mono font-medium">
                        {selectedLog.details.ip_address || 'No disponible'}
                      </p>
                    </div>
                    <div className="p-3 bg-background/50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">Navegador</Label>
                      <p className="font-medium">
                        {selectedLog.details.browser || 'Desconocido'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-background/50 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Razón del Fallo</Label>
                    <p className="font-medium">
                      {selectedLog.details.reason === 'wrong_password' ? 'Contraseña incorrecta' :
                       selectedLog.details.reason === 'user_not_found' ? 'Usuario no existe' :
                       selectedLog.details.reason || 'Error de autenticación'}
                    </p>
                  </div>
                  
                  {selectedLog.details.user_agent && (
                    <div className="p-3 bg-background/50 rounded-lg">
                      <Label className="text-xs text-muted-foreground">User Agent Completo</Label>
                      <p className="text-xs font-mono text-muted-foreground break-all">
                        {selectedLog.details.user_agent}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Regular log display */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Fecha</Label>
                  <p className="mt-1 font-medium">{formatDate(selectedLog.date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Usuario</Label>
                  <p className="mt-1 font-medium">{selectedLog.user_name || 'Sistema'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Acción</Label>
                  <p className="mt-1">
                    <Badge className={getActionBadgeColor(selectedLog.action)}>
                      {getActionLabel(selectedLog.action)}
                    </Badge>
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Entidad</Label>
                  <p className="mt-1 font-medium">{getEntityLabel(selectedLog.entity)}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Descripción</Label>
                <p className="mt-1">{selectedLog.description}</p>
              </div>

              {selectedLog.justification && (
                <div>
                  <Label className="text-muted-foreground">Justificación</Label>
                  <p className="mt-1">{selectedLog.justification}</p>
                </div>
              )}

              {selectedLog.old_values && (
                <div>
                  <Label className="text-muted-foreground">Valores Anteriores</Label>
                  <pre className="mt-1 p-3 bg-muted/30 rounded-md text-xs overflow-auto max-h-32">
                    {JSON.stringify(selectedLog.old_values, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.new_values && (
                <div>
                  <Label className="text-muted-foreground">Valores Nuevos</Label>
                  <pre className="mt-1 p-3 bg-muted/30 rounded-md text-xs overflow-auto max-h-32">
                    {JSON.stringify(selectedLog.new_values, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restore Modal */}
      <Dialog open={isRestoreModalOpen} onOpenChange={setIsRestoreModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <RotateCcw className="h-5 w-5" />
              Restaurar Movimiento
            </DialogTitle>
            <DialogDescription>
              Se restaurará el movimiento y se volverán a aplicar los cambios de stock. Se requiere autorización de administrador.
            </DialogDescription>
          </DialogHeader>
          
          {selectedMovement && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getMovementTypeColor(selectedMovement.type)}>
                    {getMovementTypeLabel(selectedMovement.type)}
                  </Badge>
                  <span className="text-sm">{selectedMovement.responsible_name}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Fecha: {formatDate(selectedMovement.date)} | {selectedMovement.items?.length || 0} equipos
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Usuario Administrador *</Label>
                  <Input
                    value={adminCreds.admin_username}
                    onChange={(e) => setAdminCreds({ ...adminCreds, admin_username: e.target.value })}
                    placeholder="Usuario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contraseña *</Label>
                  <PasswordInput
                    value={adminCreds.admin_password}
                    onChange={(e) => setAdminCreds({ ...adminCreds, admin_password: e.target.value })}
                    placeholder="Contraseña"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRestoreModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRestore} disabled={saving} className="btn-primary">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Restaurar Movimiento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditPage;
